/* eslint-disable max-len */
import type { TefComsCustomerAttribute } from '@mfe/components/tef-coms-customer-attribute';
import { CustomerAttribute, CustomerAttributeGroupItem } from '@mfec/customerAttributes/types';
import { html } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { templateContent } from 'lit/directives/template-content.js';
import { TefComsCustomerAttributeBase } from '@mfec/customerAttributes/TefComsCustomerAttributeBase';
import { TefComsCustomerAttributeInline } from '@mfe/components/tef-coms-customer-attribute-inline'; // eslint-disable-line import/no-cycle
import { localDate, localTime } from '../util';

export function getCustomerAttributesWithBulletpointTemplateResult(customerAttribute: CustomerAttribute, type: 'standard' | 'extra') {
  return customerAttribute[type].items.map((item: CustomerAttributeGroupItem) => html`
    <li class="coms-consent">${unsafeHTML(item.title)}</li>
  `);
}

export function getCustomerAttributesWithIconTemplateResult(customerAttribute: CustomerAttribute) {
  // Here granted=undefined should NOT be displayed
  return customerAttribute.extra.items.filter((item) => item.display).map((item: CustomerAttributeGroupItem) => html`
    <li><tef-icon icon="${item.granted ? 'check' : 'cross'}"></tef-icon>${unsafeHTML(item.title)}</li>`);
}

export function getCustomerAttributesWithCheckboxTemplateResult(customerAttribute: CustomerAttribute) {
  return customerAttribute.extra.items.map((item: CustomerAttributeGroupItem) => html`
    <div class="form-group-checkbox form-group-checkbox--size-small">
      <input id=${item.id} type="checkbox" .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}"></input>
      <label for=${item.id}>${unsafeHTML(item.title)}</label>
    </div>
  `);
}

export function getExtraCustomerAttributeItemsTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  switch (itemsDisplayStyle) {
    case 'bulletpoint':
      return html`
        <ul class="list">${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'extra')}</ul>`;
    case 'icon':
      return html`<ul class="list--icon">${getCustomerAttributesWithIconTemplateResult(customerAttribute)}</ul>`;
    case 'checkbox':
      return html`
        <div class="form-row form-row--group">${getCustomerAttributesWithCheckboxTemplateResult(customerAttribute)}</div>`;
    default:
      return null;
  }
}

export function getLastChangeTemplateResult(customerAttribute: CustomerAttribute, itemsDisplayStyle: string) {
  if (itemsDisplayStyle !== 'bulletpoint' && customerAttribute.date) {
    return html`
      <p>
        <small>
          <tef-coms-icon icon="clock"></tef-coms-icon>
          Letzte Änderung am ${localDate(customerAttribute.date)} um ${localTime(customerAttribute.date)} Uhr
        </small>
      </p>
    `;
  }
  return null;
}

export function getButtonsTemplateResult(tefComsCustomerAttribute: TefComsCustomerAttributeBase, itemsDisplayStyle: string) {
  if (itemsDisplayStyle === 'checkbox') {
    return html`
      <div class="section-buttons action-primary" slot="action-primary">
        <button class="button button-default" data-modal-close @click=${() => { tefComsCustomerAttribute.save(); }}>Weiter</button>
      </div>
      <div class="section-buttons action-secondary" slot="action-secondary">
        <button class="button button-border" data-modal-close @click=${() => { tefComsCustomerAttribute.cancel(); }}>Abbrechen</button>
      </div>
    `;
  }
  return html`
   <div class="section-buttons action-primary" slot="action-secondary">
      <button class="button button-default" data-modal-close>Zurück</button>
    </div>
  `;
}

export const tefComsCustomerAttributeModalDialog = function render(
  this: TefComsCustomerAttributeBase,
  customerAttribute: CustomerAttribute,
  itemsDisplayStyle: string,
  template$: HTMLTemplateElement,
) {
  return html`
    <tef-modal id="${this.modalId}" cloak>
      <div slot="title"></div>
      <div slot="body">
        <div class="typography-display--6">${templateContent(template$)}</div>
        <p><em>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</em></p>
        <ul class="list">${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}</ul>
        <p><em>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</em></p>
        ${getExtraCustomerAttributeItemsTemplateResult(customerAttribute, itemsDisplayStyle)}
        ${getLastChangeTemplateResult(customerAttribute, itemsDisplayStyle)}
        <p>${unsafeHTML(customerAttribute.footer)}</p>
      </div>
      ${getButtonsTemplateResult(this, itemsDisplayStyle)}
    </tef-modal>
  `;
};

export const tefComsCustomerAttributeModalTrigger = function render(
  this: TefComsCustomerAttribute,
  template$: HTMLTemplateElement,
) {
  return html`
    <tef-modal-trigger modal-id="tef-coms-customer-attribute-${this.id}">
      <a href='#'>${templateContent(template$)}</a>
    </tef-modal-trigger>`;
};

//
// Displays ONE customer attributes inline instead of in a modal dialog
//
export const tefComsCustomerAttributeInline = function render(
  this: TefComsCustomerAttributeInline,
  customerAttribute: CustomerAttribute,
) {
  const checkboxesHtml = customerAttribute.extra.items.map((item: CustomerAttributeGroupItem) => html`
    <div class="form-group-checkbox form-group-checkbox--size-small">
      <input id=${item.id} type="checkbox" .checked=${item.granted} data-cattr-id="${customerAttribute.id}" data-cattr-item-id="${item.id}" ?readonly="${!item.display}" ?disabled="${!item.display}"></input>
      <label for=${item.id}>${unsafeHTML(item.title)}</label>
    </div>
  `);

  return html`
    <div id="tef-coms-customer-attribute-inline">
      <p><em>${unsafeHTML(customerAttribute.standard.title || 'Standardprodukte')}</em></p>
      <ul class="list">${getCustomerAttributesWithBulletpointTemplateResult(customerAttribute, 'standard')}</ul>
      <p><em>${unsafeHTML(customerAttribute.extra.title || 'Zusatzprodukte')}</em></p>
      <div class="form-row form-row--group">${checkboxesHtml}</div>
      ${getLastChangeTemplateResult(customerAttribute, 'checkbox')}
      <p>${unsafeHTML(customerAttribute.footer)}</p>
      <div class="form-row--buttons">
        <button data-test-id="cancel-button" class="button button-border" @click=${() => { this.cancel(); }}>Abbrechen</button>
        <button data-test-id="save-button" class="button button-default" @click=${() => { this.save(); }} ?disabled=${!this.customerAttributeItemsStatusChanged}>Speichern</button>
      </div>
    </div>
  `;
};
